// store/dashboardApi/dashboardSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  fetchAdminOverview,
  fetchTopPerformers,
  fetchAdminActivityFeed,
  fetchUserDashboardOverview,
  fetchUserTaskStats,
  fetchUserPerformance,
  fetchUserRecentTasks,
  fetchUserActivity,
  createActivityLog,
} from "./dashboardThunk";

const initialState = {
  // Admin Dashboard
  adminOverview: null,
  topPerformers: [],
  adminActivityFeed: [],

  // User Dashboard
  userOverview: null,
  userTaskStats: null,
  userPerformance: null,
  userRecentTasks: [],
  userActivity: [],

  // Loading states
  adminOverviewLoading: false,
  topPerformersLoading: false,
  adminActivityLoading: false,
  userOverviewLoading: false,
  userStatsLoading: false,
  userPerformanceLoading: false,
  userTasksLoading: false,
  userActivityLoading: false,

  // Error states
  adminOverviewError: null,
  topPerformersError: null,
  adminActivityError: null,
  userOverviewError: null,
  userStatsError: null,
  userPerformanceError: null,
  userTasksError: null,
  userActivityError: null,

  status: "idle",
  error: null,
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    clearDashboardData: (state) => {
      return initialState;
    },
    clearErrors: (state) => {
      state.adminOverviewError = null;
      state.topPerformersError = null;
      state.adminActivityError = null;
      state.userOverviewError = null;
      state.userStatsError = null;
      state.userPerformanceError = null;
      state.userTasksError = null;
      state.userActivityError = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Admin Overview
      .addCase(fetchAdminOverview.pending, (state) => {
        state.adminOverviewLoading = true;
        state.adminOverviewError = null;
      })
      .addCase(fetchAdminOverview.fulfilled, (state, action) => {
        state.adminOverviewLoading = false;
        state.adminOverview = action.payload;
        state.status = "success";
      })
      .addCase(fetchAdminOverview.rejected, (state, action) => {
        state.adminOverviewLoading = false;
        state.adminOverviewError = action.payload || action.error.message;
        state.status = "failed";
      })

      // Top Performers
      .addCase(fetchTopPerformers.pending, (state) => {
        state.topPerformersLoading = true;
        state.topPerformersError = null;
      })
      .addCase(fetchTopPerformers.fulfilled, (state, action) => {
        state.topPerformersLoading = false;
        state.topPerformers = action.payload.performers || [];
        state.status = "success";
      })
      .addCase(fetchTopPerformers.rejected, (state, action) => {
        state.topPerformersLoading = false;
        state.topPerformersError = action.payload || action.error.message;
        state.status = "failed";
      })

      // Admin Activity Feed
      .addCase(fetchAdminActivityFeed.pending, (state) => {
        state.adminActivityLoading = true;
        state.adminActivityError = null;
      })
      .addCase(fetchAdminActivityFeed.fulfilled, (state, action) => {
        state.adminActivityLoading = false;
        state.adminActivityFeed = action.payload || [];
        state.status = "success";
      })
      .addCase(fetchAdminActivityFeed.rejected, (state, action) => {
        state.adminActivityLoading = false;
        state.adminActivityError = action.payload || action.error.message;
        state.status = "failed";
      })

      // User Dashboard Overview
      .addCase(fetchUserDashboardOverview.pending, (state) => {
        state.userOverviewLoading = true;
        state.userOverviewError = null;
      })
      .addCase(fetchUserDashboardOverview.fulfilled, (state, action) => {
        state.userOverviewLoading = false;
        state.userOverview = action.payload;
        state.status = "success";
      })
      .addCase(fetchUserDashboardOverview.rejected, (state, action) => {
        state.userOverviewLoading = false;
        state.userOverviewError = action.payload || action.error.message;
        state.status = "failed";
      })

      // User Task Stats
      .addCase(fetchUserTaskStats.pending, (state) => {
        state.userStatsLoading = true;
        state.userStatsError = null;
      })
      .addCase(fetchUserTaskStats.fulfilled, (state, action) => {
        state.userStatsLoading = false;
        state.userTaskStats = action.payload;
        state.status = "success";
      })
      .addCase(fetchUserTaskStats.rejected, (state, action) => {
        state.userStatsLoading = false;
        state.userStatsError = action.payload || action.error.message;
        state.status = "failed";
      })

      // User Performance
      .addCase(fetchUserPerformance.pending, (state) => {
        state.userPerformanceLoading = true;
        state.userPerformanceError = null;
      })
      .addCase(fetchUserPerformance.fulfilled, (state, action) => {
        state.userPerformanceLoading = false;
        state.userPerformance = action.payload;
        state.status = "success";
      })
      .addCase(fetchUserPerformance.rejected, (state, action) => {
        state.userPerformanceLoading = false;
        state.userPerformanceError = action.payload || action.error.message;
        state.status = "failed";
      })

      // User Recent Tasks
      .addCase(fetchUserRecentTasks.pending, (state) => {
        state.userTasksLoading = true;
        state.userTasksError = null;
      })
      .addCase(fetchUserRecentTasks.fulfilled, (state, action) => {
        state.userTasksLoading = false;
        state.userRecentTasks = action.payload || [];
        state.status = "success";
      })
      .addCase(fetchUserRecentTasks.rejected, (state, action) => {
        state.userTasksLoading = false;
        state.userTasksError = action.payload || action.error.message;
        state.status = "failed";
      })

      // User Activity
      .addCase(fetchUserActivity.pending, (state) => {
        state.userActivityLoading = true;
        state.userActivityError = null;
      })
      .addCase(fetchUserActivity.fulfilled, (state, action) => {
        state.userActivityLoading = false;
        state.userActivity = action.payload || [];
        state.status = "success";
      })
      .addCase(fetchUserActivity.rejected, (state, action) => {
        state.userActivityLoading = false;
        state.userActivityError = action.payload || action.error.message;
        state.status = "failed";
      })

      // Create Activity Log
      .addCase(createActivityLog.pending, (state) => {
        state.status = "loading";
      })
      .addCase(createActivityLog.fulfilled, (state, action) => {
        state.status = "success";
        // Optionally add the new activity to the feed
        if (state.userActivity) {
          state.userActivity.unshift(action.payload);
        }
      })
      .addCase(createActivityLog.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      });
  },
});

export const { clearDashboardData, clearErrors } = dashboardSlice.actions;
export default dashboardSlice.reducer;
